<template>
  <div class="deploy-container">
    <b-card no-body v-if="!isLoadingData">
      <div class="dv-row">
        <div class="dv-label">{{ messages.DEPLOY_VERSION.Text_vod__c }}</div>
        <div class="dv-dd">
          <b-form-select
            @change="onVersionChange"
            v-model="selectedVersion"
            :options="dpVersions"
            size="sm"
          ></b-form-select>
        </div>
      </div>

      <div class="deployment-section" style="overflow-x:auto;">
        <b-table
          v-if="dpError"
          striped
          hover
          :fields="fields"
          :items="records"
          :empty-text="messages.FAILED_TO_LOAD_DEPLOYMENTS.Text_vod__c"
          show-empty
        >
          <template #empty="scope">
            <div>{{ scope.emptyText }}</div>
          </template>
        </b-table>
        <b-table v-else striped hover :fields="fields" :items="records">
          <template #cell(action)="data">
            <div
              class="create-tcell"
              v-if="data.item.selectedAction === 'CREATE'"
            >
              <div class="create-text">{{ messages.Create.Text_vod__c }}</div>
              <div class="delete-wrapper">
                <svg
                  class="delete-row-icon"
                  tabindex="0"
                  @keydown.enter.stop="
                    deleteRow(data.item.Id, data.item.actionId)
                  "
                  @click="deleteRow(data.item.Id, data.item.actionId)"
                >
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#delete"
                  ></use>
                </svg>
              </div>
            </div>
            <b-form-select
              v-if="data.item.selectedAction !== 'CREATE'"
              v-model="data.item.selectedAction"
              :options="actions"
              @change="actionChanged(data.item.selectedAction, data.item.Id)"
            ></b-form-select>
          </template>

          <template #cell(Name)="data" v-if="nameFLS.hasRead">
            <text-input
              v-if="isRowEditable(data.item.selectedAction) && nameFLS.hasEdit"
              v-model="data.item.Name"
              :focus="shouldFocus(data.item)"
              :id="'name-' + (data.item.Id || data.item.actionId)"
              type="text"
              @on-text-input-change="nameChanged"
            />
            <div v-else>
              {{ data.item.Name }}
            </div>
          </template>

          <template #cell(RecordTypeId)="data">
            <div
              v-if="
                isRowEditable(data.item.selectedAction) &&
                  typesForDisplay.length === 0 &&
                  recordTypeFLS.hasEdit
              "
            >
              {{ messages.RECORD_TYPES_MISSING.Text_vod__c }}
            </div>
            <div
              v-else-if="
                isRowEditable(data.item.selectedAction) &&
                  typesForDisplay.length === 1 &&
                  recordTypeFLS.hasEdit
              "
              class="record-type-id-col"
            >
              {{ data.item.type.label }}
            </div>
            <b-form-select
              class="record-type-id-col"
              v-else-if="
                isRowEditable(data.item.selectedAction) &&
                  typesForDisplay.length > 1 &&
                  recordTypeFLS.hasEdit
              "
              v-model="data.item.type.label"
              :options="typesForDisplay"
              @change="onRTSelected(data.item.type.label, data.item.actionId)"
            ></b-form-select>
            <div v-else>
              {{ data.item.type.label }}
            </div>
          </template>

          <template #head(RecordTypeId)="data" v-if="recordTypeFLS.hasRead">
            <div>
              <span>{{ data.label }}</span>
              <a :href="data.field.link" target="_blank">
                <svg class="info-icon">
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#info_alt"
                  ></use>
                </svg>
              </a>
            </div>
          </template>

          <template
            #cell(Profile_Name_vod__c)="data"
            v-if="reportProfileNameFLS.hasRead"
          >
            <search-box
              v-if="
                isRowEditable(data.item.selectedAction) &&
                  reportProfileNameFLS.hasEdit
              "
              :id="'profile-' + (data.item.Id || data.item.actionId)"
              :box-type="'deEditModal'"
              :has-icon="false"
              :is-read-only="!reportProfileNameFLS.hasEdit"
              :label-only="true"
              :list="profilesForDisplay"
              :value="data.item.profile"
              @on-search-box-item-selected="onProfileSelected"
            />

            <div v-else>
              {{ data.item.profile.label }}
            </div>
          </template>

          <template
            #cell(Object_RecordType_vod__c)="data"
            v-if="reportORTNameFLS.hasRead"
          >
            <search-box
              v-if="
                isRowEditable(data.item.selectedAction) &&
                  reportORTNameFLS.hasEdit
              "
              :id="'objRTs-' + (data.item.Id || data.item.actionId)"
              :box-type="'deEditModal'"
              :has-icon="false"
              :is-read-only="!reportORTNameFLS.hasEdit"
              :label-only="true"
              :list="objRTs"
              :value="data.item.objRT"
              @on-search-box-item-selected="onObjRTSelected"
            />
            <div v-else>
              {{ data.item.objRT.label }}
            </div>
          </template>

          <template #cell(Platform_vod__c)="data" v-if="platformFLS.hasRead">
            <search-box
              class="multi-select"
              v-if="isReadOnlyPlatform(data.item)"
              :id="'platform-' + (data.item.Id || data.item.actionId)"
              :is-read-only="true"
              :multi-select="true"
              :box-type="'deEditModal'"
              :has-icon="false"
              :list="platformValues"
              :values="data.item.selectedPlatforms"
              @on-search-box-item-selected="onPlatformSelected"
            />

            <search-box
              class="multi-select"
              v-else-if="
                isRowEditable(data.item.selectedAction) && platformFLS.hasEdit
              "
              :id="'platform-' + (data.item.Id || data.item.actionId)"
              :multi-select="true"
              :box-type="'deEditModal'"
              :has-icon="false"
              :list="platformValues"
              :values="data.item.selectedPlatforms"
              @on-search-box-item-selected="onPlatformSelected"
            />
          </template>

          <template #head(Platform_vod__c)="data" v-if="platformFLS.hasRead">
            <div>
              <span>{{ data.label }}</span>
              <a :href="data.field.link" target="_blank">
                <svg class="info-icon">
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#info_alt"
                  ></use>
                </svg>
              </a>
            </div>
          </template>

          <template #cell(Phone_vod__c)="data" v-if="phoneFLS.hasRead">
            <b-form-checkbox
              :disabled="
                !isRowEditable(data.item.selectedAction) || !phoneFLS.hasEdit
              "
              v-model="data.item.dpPhone"
            ></b-form-checkbox>
          </template>

          <template #head(Phone_vod__c)="data" v-if="phoneFLS.hasRead">
            <div>
              <span>{{ data.label }}</span>
              <a :href="data.field.link" target="_blank">
                <svg class="info-icon">
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#info_alt"
                  ></use>
                </svg>
              </a>
            </div>
          </template>

          <template
            #cell(External_Id_vod__c)="data"
            v-if="externalIdFLS.hasRead"
          >
            <text-input
              v-if="
                isRowEditable(data.item.selectedAction) && externalIdFLS.hasEdit
              "
              v-model="data.item.External_Id_vod__c"
              :placeholder="
                generateExternalId(
                  data.item.Name,
                  data.item.Profile_Name_vod__c,
                  data.item.Object_RecordType_vod__c
                )
              "
              :id="'external-id-' + (data.item.Id || data.item.actionId)"
              type="text"
              @on-text-input-change="externalIdChanged"
              @on-text-input-blur="externalIdDefocused"
            />
            <div v-else>
              {{ data.item.External_Id_vod__c }}
            </div>
          </template>

          <template
            #cell(Sharing_Group_vod__c)="data"
            v-if="sharingGroupFLS.hasRead"
          >
            <text-input
              v-if="
                isRowEditable(data.item.selectedAction) &&
                  sharingGroupFLS.hasEdit
              "
              v-model="data.item.Sharing_Group_vod__c"
              :id="'sharing-group-' + (data.item.Id || data.item.actionId)"
              type="text"
              @on-text-input-change="sharingGroupChanged"
            />
            <div v-else>
              {{ data.item.Sharing_Group_vod__c }}
            </div>
          </template>

          <template
            #cell(Visibility_Criteria_vod__c)="data"
            v-if="visibilityCriteriaFLS.hasRead"
          >
            <text-input
              v-if="
                isRowEditable(data.item.selectedAction) &&
                  visibilityCriteriaFLS.hasEdit
              "
              v-model="data.item.Visibility_Criteria_vod__c"
              :id="
                'visibility-criteria-' + (data.item.Id || data.item.actionId)
              "
              type="text"
              @on-text-input-change="visibilityCriteriaChanged"
            />
            <div v-else>
              {{ data.item.Visibility_Criteria_vod__c }}
            </div>
          </template>

          <template
            #cell(Studio_Version_vod__c)="data"
            v-if="studioVersionFLS.hasRead"
          >
            <div class="studio-version">
              {{ data.item.Studio_Version_vod__c }}
              <div
                class="version-match-icon-container"
                v-if="
                  !versionsMatchFail && data.item.selectedAction !== 'CREATE'
                "
              >
                <svg
                  class="check-icon version-match-icon"
                  v-if="!versionsMatchLoading && versionsMatch[data.item.Id]"
                >
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#check"
                  ></use>
                </svg>
                <svg
                  class="error-icon version-match-icon"
                  v-if="!versionsMatchLoading && !versionsMatch[data.item.Id]"
                >
                  <use
                    xlink:href="../assets/icons/utility-sprite/svg/symbols.svg#error"
                  ></use>
                </svg>
              </div>
              <div
                class="loader loader-small"
                v-if="versionsMatchLoading && !versionsMatchFail"
              ></div>
            </div>
          </template>

          <template #cell(status)="data">
            <div class="studio-status">
              <div class="d-loader loader" v-if="data.item.isDeploying"></div>
              <div
                class="s-status"
                v-if="
                  !data.item.isDeploying && data.item.deployStatus === 'success'
                "
              >
                {{ messages.Success.Text_vod__c }}
              </div>
              <div
                class="s-status generic_exclamation"
                v-if="
                  !data.item.isDeploying && data.item.deployStatus === 'fail'
                "
              ></div>
            </div>
          </template>

          <template #cell(Status_vod__c)="data" v-if="statusFLS.hasRead">
            <div class="publish-status">
              {{ statusLabelMap[data.item.Status_vod__c] }}
            </div>
          </template>

          <template
            #cell(Failure_Msg_vod__c)="data"
            v-if="statusDetailsFLS.hasRead"
          >
            <div class="failure-message">
              {{ data.item.Failure_Msg_vod__c }}
            </div>
          </template>
        </b-table>
      </div>

      <div class="btn-wrapper" v-if="hasRequiredFLS">
        <button
          class="slds-button slds-button_outline-brand"
          @click="newRecord"
          :disabled="!org.isSandbox && currentApp.isTemplate"
        >
          {{ messages.NEW.Text_vod__c }}
        </button>
        <button
          :disabled="typesForDisplay.length === 0"
          class="slds-button slds-button_outline-brand deploy-btn"
          @click="doDeploy"
        >
          {{ messages.DEPLOY.Text_vod__c }}
        </button>
      </div>
    </b-card>
    <deploy-confirm-modal
      v-if="!isLoadingData"
      :app-id="appId"
      :has-profile-id-edit-permission="reportProfileIdFLS.hasEdit"
      :has-profile-name-edit-permission="reportProfileNameFLS.hasEdit"
      :has-object-record-type-id-edit-permission="reportORTIdFLS.hasEdit"
      :has-object-record-type-name-edit-permission="reportORTNameFLS.hasEdit"
      :has-platform-edit-permission="platformFLS.hasEdit"
      :has-phone-edit-permission="phoneFLS.hasEdit"
      :has-external-id-edit-permission="externalIdFLS.hasEdit"
      :has-visibility-criteria-edit-permission="visibilityCriteriaFLS.hasEdit"
      :has-sharing-group-edit-permission="sharingGroupFLS.hasEdit"
      :selected-version="selectedVersion"
      :to-deploy="recordsToBeModified"
      :is-from-template="currentApp.isTemplate"
      @on-deploy-records="onDeployRecords"
      @done-deploy-records="doneDeployRecords"
    ></deploy-confirm-modal>
    <div class="loader" v-if="isLoadingData"></div>
  </div>
</template>

<script>
  import { v4 as uuidv4 } from "uuid";
  import sortBy from "lodash.sortby";
  import { DateTime } from "luxon";
  import { mapState } from "vuex";
  import services from "@/services";
  import SearchBox from "@/components/SearchBox";
  import DeployConfirmModal from "@/components/DeployConfirmModal";
  import utils from "@/utils";
  import {
    APP_TYPES_TO_OBJECT_RECORD_TYPES_MAPPING,
    ACTION_LABELS,
    HTML_REPORT_FIELD_NAMES,
    REQUIRED_FLS_TO_DEPLOY,
    CREATE_WIZARD_SEARCH_MAP,
    TOAST_CONFIG,
    VISIBILITY_CRITERIA_TYPES
  } from "@/static/global-variables";
  import { QUERIES } from "@/static/queries";
  import TextInput from "@/components/TextInput";

  export default {
    name: "Deploy",
    components: {
      TextInput,
      SearchBox,
      DeployConfirmModal
    },
    data() {
      return {
        dpRecords: [],
        dpVersions: [],
        recordsToBeModified: [],
        profiles: [],
        objRTs: [],
        selectedVersion: null,
        versionsLoading: true,
        types: [],
        dpLoading: true,
        profilesLoading: true,
        objRTLoading: true,
        dpError: null,
        deployStatusSnapshot: null,
        versionsMatch: {},
        versionsMatchLoading: true,
        versionsMatchFail: false,
        originalDeployments: {}
      };
    },
    props: {
      appId: String
    },
    created() {
      this.init();
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages",
        org: "org",
        htmlReportFieldLabels: "htmlReportFieldLabels",
        objectDescribe: "objectDescribe"
      }),
      actionText() {
        return this.getMessageText(this.messages.ACTION);
      },
      isLoadingData() {
        return (
          this.versionsLoading ||
          this.dpLoading ||
          this.profilesLoading ||
          this.objRTLoading
        );
      },
      platformValues() {
        let output = [];
        const platformField = utils.findFieldByName(
          this.objectDescribe[QUERIES.htmlReport.object].fields,
          HTML_REPORT_FIELD_NAMES.Platform_vod__c
        );

        if (platformField && platformField.picklistValues) {
          output = platformField.picklistValues.map(p => {
            p.name = p.value;
            return p;
          });
        }

        return output;
      },
      statusLabelMap() {
        let output = {};
        const statusField = utils.findFieldByName(
          this.objectDescribe[QUERIES.htmlReport.object].fields,
          HTML_REPORT_FIELD_NAMES.Status_vod__c
        );

        if (statusField && statusField.picklistValues) {
          statusField.picklistValues.forEach(p => {
            output[p.value] = p.label;
          });
        }

        return output;
      },
      hasRequiredFLS() {
        let output = true;
        for (
          let i = 0;
          i < REQUIRED_FLS_TO_DEPLOY.HTML_Report_vod__c.length;
          i++
        ) {
          const field = utils.findFieldFLS(
            this.objectDescribe[QUERIES.htmlReport.object],
            REQUIRED_FLS_TO_DEPLOY.HTML_Report_vod__c[i]
          );
          if (!field.hasEdit) {
            output = false;
            break;
          }
        }

        return output;
      },
      platformFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Platform_vod__c
        );
      },
      statusFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Status_vod__c
        );
      },
      statusDetailsFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Failure_Msg_vod__c
        );
      },
      reportProfileIdFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Profile_vod__c
        );
      },
      reportProfileNameFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Profile_Name_vod__c
        );
      },
      reportORTIdFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Object_RecordType_Id_vod__c
        );
      },
      reportORTNameFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Object_RecordType_vod__c
        );
      },
      phoneFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Phone_vod__c
        );
      },
      studioVersionFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Studio_Version_vod__c
        );
      },
      nameFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Name
        );
      },
      recordTypeFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.RecordType
        );
      },
      externalIdFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.External_Id_vod__c
        );
      },
      visibilityCriteriaFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Visibility_Criteria_vod__c
        );
      },
      sharingGroupFLS() {
        return utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Sharing_Group_vod__c
        );
      },
      fields() {
        let columns = [
          {
            key: "action",
            label: this.actionText,
            class: "action-col"
          }
        ];

        const last = [
          {
            key: "status",
            label: this.$t("deploymentStatusHeader"),
            class: "status-col"
          }
        ];

        let middleCols = this.htmlReportFieldLabels;
        let invalidFields = [];

        if (!this.reportProfileNameFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Profile_Name_vod__c);
        }

        if (!this.reportORTNameFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Object_RecordType_vod__c);
        }

        if (!this.phoneFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Phone_vod__c);
        }

        if (!this.platformFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Platform_vod__c);
        }

        if (!this.statusFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Status_vod__c);
        }

        if (!this.statusDetailsFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Failure_Msg_vod__c);
        }

        if (!this.studioVersionFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Studio_Version_vod__c);
        }

        if (!this.nameFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Name);
        }

        if (!this.recordTypeFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.RecordType);
        }

        if (!this.externalIdFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.External_Id_vod__c);
        }

        if (
          !this.visibilityCriteriaFLS.hasRead ||
          !VISIBILITY_CRITERIA_TYPES.includes(this.currentApp.type)
        ) {
          invalidFields.push(
            HTML_REPORT_FIELD_NAMES.Visibility_Criteria_vod__c
          );
        }

        if (!this.sharingGroupFLS.hasRead) {
          invalidFields.push(HTML_REPORT_FIELD_NAMES.Sharing_Group_vod__c);
        }

        middleCols = this.htmlReportFieldLabels.filter(
          field => !invalidFields.includes(field.key)
        );

        const cols = columns.concat(middleCols).concat(last);
        let publishStatusIndex = -1;
        let publishStatusDetailsIndex = -1;

        for (let i = 0; i < cols.length; i++) {
          if (cols[i].key === HTML_REPORT_FIELD_NAMES.Status_vod__c) {
            publishStatusIndex = i;
            cols[i].label = this.$t("publishingStatus");
          }

          if (cols[i].key === HTML_REPORT_FIELD_NAMES.Failure_Msg_vod__c) {
            publishStatusDetailsIndex = i;
            cols[i].label = this.$t("publishingStatusDetails");
          }
        }

        if (publishStatusIndex >= 0) {
          cols.push(cols.splice(publishStatusIndex, 1)[0]);
        }

        if (publishStatusDetailsIndex >= 0) {
          if (publishStatusIndex >= 0) {
            publishStatusDetailsIndex -= 1;
          }

          cols.push(cols.splice(publishStatusDetailsIndex, 1)[0]);
        }

        return cols;
      },
      platformMap() {
        let mapping = {};
        this.platformValues.forEach(platform => {
          mapping[platform.value] = platform;
        });
        return mapping;
      },
      profilesForDisplay() {
        return this.profiles;
      },
      typesForDisplay() {
        if (this.types.length === 0) {
          return [];
        }
        return this.types.map(type => {
          return type.label;
        });
      },
      actions() {
        const normal = [
          { value: "noAction", text: "" },
          {
            value: ACTION_LABELS.update,
            text: this.getMessageText(this.messages.UPDATE)
          }
        ];

        const removeOnly = [
          {
            value: ACTION_LABELS.remove,
            text: this.getMessageText(this.messages.REMOVE)
          }
        ];

        return !this.org.isSandbox && this.currentApp.isTemplate
          ? removeOnly
          : normal.concat(removeOnly);
      },
      records() {
        return this.dpRecords;
      },
      currentApp() {
        return utils.findAppByAppId(this.apps, this.appId);
      }
    },
    methods: {
      init() {
        let component = this;
        component.recordsToBeModified = [];
        const userLocale = utils.getStandardLocalFormat(
          this.$store.getters.userLocale
        );
        const fieldFLS = {
          includeProfileName: component.reportProfileNameFLS.hasRead,
          includeORTName: component.reportORTNameFLS.hasRead,
          includeORTId: component.reportORTIdFLS.hasRead,
          includePlatform: component.platformFLS.hasRead,
          includeStudioVersion: component.studioVersionFLS.hasRead,
          includePhone: component.phoneFLS.hasRead,
          includeStatus: component.statusFLS.hasRead,
          includeStatusDetail: component.statusDetailsFLS.hasRead,
          includeRecordType: component.recordTypeFLS.hasRead,
          includeExternalId: component.externalIdFLS.hasRead,
          includeVisibilityCriteria: component.visibilityCriteriaFLS.hasRead,
          includeSharingGroup: component.sharingGroupFLS.hasRead,
          includeStudioId: true
        };
        services.fetchAppTypes().then(
          response => {
            if (!response || !Array.isArray(response.records)) {
              this.types = [];
              this.displayErrorMessage(
                this.getMessageText(this.messages.SFDC_METADATA_ERROR)
              );
            } else {
              services.fetchDeploymentsForApp(component.appId, fieldFLS).then(
                resp => {
                  if (!Array.isArray(resp.records)) {
                    component.dpRecords = [];
                    component.dpError = resp;
                  } else {
                    component.dpRecords = resp.records.map(record => {
                      record.selectedAction = "noAction";

                      record.actionId = uuidv4();

                      record.profile = {
                        label: record.Profile_Name_vod__c,
                        id: record.Profile_vod__c
                      };

                      let type = response.records.find(
                        element => element.Id === record.RecordTypeId
                      );
                      const idToLabelMap = this.getRecordTypeLabels(
                        this.objectDescribe.HTML_Report_vod__c
                      );

                      let label = idToLabelMap.has(record.RecordTypeId)
                        ? idToLabelMap.get(record.RecordTypeId).name
                        : type.DeveloperName;
                      record.type = {
                        devName: type.DeveloperName,
                        label: label
                      };

                      record.objRT = {
                        label: record.Object_RecordType_vod__c,
                        id: record.Object_RecordType_Id_vod__c
                      };

                      if (record.Platform_vod__c) {
                        record.selectedPlatforms = record.Platform_vod__c.split(
                          ";"
                        ).map(key => {
                          return {
                            label: component.platformMap[key].label,
                            id: key,
                            value: key,
                            name: key
                          };
                        });
                      } else {
                        record.selectedPlatforms = [];
                      }
                      record.originalPlatforms = record.selectedPlatforms.concat(
                        []
                      );

                      record.dpPhone = record.Phone_vod__c;

                      record.isDeploying = false;
                      record.deployStatus = null;

                      if (Array.isArray(component.deployStatusSnapshot)) {
                        let result = component.deployStatusSnapshot.find(r => {
                          return r.htmlReportId === record.Id;
                        });

                        if (result) {
                          record.deployStatus = result.deployStatus;
                        }
                      }

                      return record;
                    });
                  }

                  component.dpLoading = false;
                },
                err => {
                  component.dpRecords = [];
                  component.dpError = err;
                  component.dpLoading = false;
                }
              );
            }
          },
          () => {
            this.displayErrorMessage(
              this.getMessageText(this.messages.SFDC_METADATA_ERROR)
            );
          }
        );
        services.fetchVersions(component.appId).then(resp => {
          component.dpVersions = resp
            .filter(res => {
              return res.deployable;
            })
            .sort((a, b) => a.version - b.version)
            .map(res => {
              let localTime = DateTime.fromISO(res.created)
                .setLocale(userLocale)
                .toLocaleString(DateTime.DATETIME_SHORT);
              res.value = res.version;
              res.text = `${res.version} (${localTime})`;
              return res;
            });
          component.selectedVersion = Math.max(
            ...this.dpVersions.map(v => v.value)
          );
          component.versionsLoading = false;
        });
        services.fetchProfiles().then(
          resp => {
            component.profiles = sortBy(resp, ["label"]);
            component.profilesLoading = false;
          },
          () => {
            component.profiles = [];
            component.profilesLoading = false;
          }
        );
        services
          .fetchObjectRecordTypes(
            APP_TYPES_TO_OBJECT_RECORD_TYPES_MAPPING[
              this.currentApp.type.replace(/ /g, "_")
            ]
          )
          .then(resp => {
            component.objRTs = sortBy(resp, ["label"]);
            component.objRTLoading = false;
          });
        this.fetchObjectLabel();
        services.fetchVersionMatch(component.appId).then(
          resp => {
            component.versionsMatch = resp;
            component.versionsMatchLoading = false;
          },
          err => {
            console.error(err);
            component.versionsMatchLoading = false;
            component.versionsMatchFail = true;
            this.$bvToast.toast(
              this.messages.ERROR_WHILE_LOADING.Text_vod__c,
              TOAST_CONFIG.BOTTOM_CENTER_ERROR
            );
          }
        );
      },
      fetchObjectLabel() {
        services.fetchObjectNames().then(
          resp => {
            let result = {
              Account: resp.Account,
              KOL: resp.Account,
              Account_Plan_vod__c: resp.Account_Plan_vod__c,
              Order_vod__c: resp.Order_vod__c,
              Inventory_Monitoring_vod__c: resp.Inventory_Monitoring_vod__c,
              EM_Speaker_vod__c: resp.EM_Speaker_vod__c,
              Call2_vod__c: resp.Call2_vod__c
            };

            this.fetchAppTypes(result);
          },
          err => {
            console.error(err);
          }
        );
      },
      fetchAppTypes(result) {
        services.fetchAppTypes().then(
          resp => {
            if (!resp || !Array.isArray(resp.records)) {
              this.types = [];
              this.displayErrorMessage(
                this.getMessageText(this.messages.SFDC_METADATA_ERROR)
              );
            } else {
              const recordTypes = utils.reformatRecordTypes(
                resp.records,
                new Map(Object.entries(result))
              );
              const filterTypes = recordTypes.filter(
                element =>
                  element.value.type === this.$store.getters.currentContent.type
              );

              this.types = this.sortRecordTypes(
                filterTypes,
                this.objectDescribe.HTML_Report_vod__c
              );
            }
          },
          () => {
            this.types = [];
            this.displayErrorMessage(
              this.getMessageText(this.messages.SFDC_METADATA_ERROR)
            );
          }
        );
      },
      sortRecordTypes(recordTypes, describeObject) {
        const idToLabelMap = this.getRecordTypeLabels(describeObject);
        const map = new Map(Object.entries(CREATE_WIZARD_SEARCH_MAP));
        let order = Array.from(map.keys());
        // Sort content types
        recordTypes.sort(function(a, b) {
          return (
            order.indexOf(a.value.devName) - order.indexOf(b.value.devName)
          );
        });
        return recordTypes
          .map(element => {
            return {
              label: idToLabelMap.get(element.value.id).name,
              devName: element.value.devName,
              available: idToLabelMap.get(element.value.id).available
            };
          })
          .filter(element => element.available);
      },
      getRecordTypeLabels(describeObject) {
        return new Map(
          describeObject.recordTypeInfos.map(object => {
            return [
              object.recordTypeId,
              { name: object.name, available: object.available }
            ];
          })
        );
      },
      isReadOnlyPlatform(item) {
        return (
          !this.isRowEditable(item.selectedAction) ||
          (this.isRowEditable(item.selectedAction) && !this.platformFLS.hasEdit)
        );
      },
      shouldHaveEmptyColumn(item) {
        return (
          (item.selectedAction === ACTION_LABELS.create &&
            !this.platformFLS.hasEdit) ||
          (item.selectedAction === ACTION_LABELS.update &&
            !item.Platform_vod__c &&
            !this.platformFLS.hasEdit)
        );
      },
      hasSelectedPlatforms(selectedPlatforms) {
        return Array.isArray(selectedPlatforms) && selectedPlatforms.length > 0;
      },
      onPlatformSelected(value, searchBoxId) {
        let val = value.concat([]);
        const key = searchBoxId.replace("platform-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        if (target && target.selectedAction === ACTION_LABELS.create) {
          target.selectedPlatforms = val;
          this.dpRecords = this.dpRecords.concat([]);
        }

        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        if (i >= 0) {
          this.recordsToBeModified[i].selectedPlatforms = val;
        } else {
          let item = JSON.parse(JSON.stringify(target));
          item.selectedPlatforms = val;
          this.recordsToBeModified.push(item);
        }
      },
      shouldFocus(item) {
        return (
          item.selectedAction === ACTION_LABELS.create ||
          item.selectedAction === ACTION_LABELS.update
        );
      },
      externalIdDependencyChangedHandler(target, newName, newProfile, newType) {
        if (target.selectedAction !== ACTION_LABELS.create) {
          return target.External_Id_vod__c;
        }

        const oldExternalId = target.External_Id_vod__c;
        const oldGeneratedExternalId = this.generateExternalId(
          target.Name,
          target.Profile_Name_vod__c,
          target.Object_RecordType_vod__c
        );

        let newExternalId;

        if (oldExternalId === oldGeneratedExternalId) {
          newExternalId = this.generateExternalId(newName, newProfile, newType);
        } else {
          newExternalId = oldExternalId;
        }
        return newExternalId;
      },
      generateExternalId(reportName, profile, objectRecordType) {
        let result = reportName;
        if (profile != null) {
          result += "__" + profile;
        }
        if (objectRecordType != null) {
          result += "__" + objectRecordType;
        }
        return result;
      },
      externalIdDefocused(field, fieldId) {
        const key = fieldId.replace("external-id-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        if (!field) {
          field = this.generateExternalId(
            target.Name,
            target.Profile_Name_vod__c,
            target.Object_RecordType_vod__c
          );
        }

        target.External_Id_vod__c = field;
        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });
        if (i >= 0) {
          this.recordsToBeModified[i].External_Id_vod__c = field;
        } else {
          this.recordsToBeModified.push(target);
        }
      },
      externalIdChanged(field, fieldId) {
        const key = fieldId.replace("external-id-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.External_Id_vod__c = field;
        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });
        if (i >= 0) {
          this.recordsToBeModified[i].External_Id_vod__c = field;
        } else {
          this.recordsToBeModified.push(target);
        }
      },

      nameChanged(name, nameId) {
        const key = nameId.replace("name-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.External_Id_vod__c = this.externalIdDependencyChangedHandler(
          target,
          name,
          target.Profile_Name_vod__c,
          target.Object_RecordType_vod__c
        );

        target.Name = name;

        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });
        if (i >= 0) {
          this.recordsToBeModified[i].Name = name;
        } else {
          this.recordsToBeModified.push(target);
        }
      },

      sharingGroupChanged(sharingGroup, sharingGroupId) {
        const key = sharingGroupId.replace("sharing-group-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.Sharing_Group_vod__c = sharingGroup;
        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });
        if (i >= 0) {
          this.recordsToBeModified[i].Sharing_Group_vod__c = sharingGroup;
        } else {
          this.recordsToBeModified.push(target);
        }
      },

      visibilityCriteriaChanged(visibilityCriteria, visibilityCriteriaId) {
        const key = visibilityCriteriaId.replace("visibility-criteria-", "");
        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.Visibility_Criteria_vod__c = visibilityCriteria;
        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });
        if (i >= 0) {
          this.recordsToBeModified[
            i
          ].Visibility_Criteria_vod__c = visibilityCriteria;
        } else {
          this.recordsToBeModified.push(target);
        }
      },

      onRTSelected(value, itemId) {
        let newValue = {};
        if (value == null) {
          let val = this.types[0];
          newValue.devName = val.devName;
          newValue.label = val.label;
        } else {
          for (let recordType of this.types) {
            let val = {};
            if (recordType.label === value) {
              val.devName = recordType.devName;
              val.label = recordType.label;
              newValue = val;
            }
          }
        }
        let target = this.dpRecords.find(rec => {
          return rec.Id === itemId || rec.actionId === itemId;
        });
        if (target === undefined) {
          return;
        }
        target.type = newValue;
        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === itemId || rec.actionId === itemId;
        });
        if (i >= 0) {
          this.recordsToBeModified[i].type = newValue;
        } else {
          this.recordsToBeModified.push(target);
        }
      },
      actionChanged(selectedAction, recordId) {
        let target = this.dpRecords.find(rec => {
          return rec.Id === recordId;
        });

        this.originalDeployments[recordId] = JSON.parse(JSON.stringify(target));

        if (selectedAction !== ACTION_LABELS.update) {
          if (target.dpPhone !== target.Phone_vod__c) {
            target.dpPhone = target.Phone_vod__c;
          }

          if (target.selectedPlatforms !== target.originalPlatforms) {
            target.selectedPlatforms = target.originalPlatforms;
          }

          let dpRecordIdx = -1;
          for (let i = 0; i < this.dpRecords.length; i++) {
            if (this.dpRecords[i].Id === recordId) {
              dpRecordIdx = i;
            }
          }

          for (let key in this.dpRecords[dpRecordIdx]) {
            if (key !== "selectedAction") {
              this.dpRecords[dpRecordIdx][key] = this.originalDeployments[
                recordId
              ][key];
            }
          }
        }

        this.recordsToBeModified = this.recordsToBeModified.filter(rec => {
          return rec.Id !== recordId;
        });

        if (selectedAction !== ACTION_LABELS.noAction) {
          this.recordsToBeModified.push(target);
        }
      },
      onProfileSelected(value) {
        let val = JSON.parse(JSON.stringify(value));
        const key = val.searchBoxId.replace("profile-", "");

        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.External_Id_vod__c = this.externalIdDependencyChangedHandler(
          target,
          target.Name,
          value.label,
          target.Object_RecordType_vod__c
        );

        if (target && target.selectedAction === ACTION_LABELS.create) {
          target.Profile_Name_vod__c = val.Name || null;
          target.Profile_vod__c = val.Id || null;
          if (val.Name) {
            target.profile = val;
            target.profile.isEmpty = false;
          }
          this.dpRecords = this.dpRecords.concat([]);
        }

        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        if (i >= 0) {
          this.recordsToBeModified[i].Profile_Name_vod__c = val.Name || null;
          this.recordsToBeModified[i].Profile_vod__c = val.Id || null;
        } else {
          let record = this.dpRecords.find(rec => {
            return rec.Id === key || rec.actionId === key;
          });

          let item = JSON.parse(JSON.stringify(record));

          item.Profile_Name_vod__c = val.Name || null;
          item.Profile_vod__c = val.Id || null;

          this.recordsToBeModified.push(item);
        }
      },
      onObjRTSelected(value) {
        let val = JSON.parse(JSON.stringify(value));
        const key = val.searchBoxId.replace("objRTs-", "");

        let target = this.dpRecords.find(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        target.External_Id_vod__c = this.externalIdDependencyChangedHandler(
          target,
          target.Name,
          target.Profile_Name_vod__c,
          value.label
        );

        if (target && target.selectedAction === ACTION_LABELS.create) {
          target.Object_RecordType_vod__c = val.DeveloperName || null;
          target.Object_RecordType_Id_vod__c = val.Id || null;
          if (val.DeveloperName) {
            target.objRT = val;
            target.objRT.isEmpty = false;
          }
          this.dpRecords = this.dpRecords.concat([]);
        }

        let i = this.recordsToBeModified.findIndex(rec => {
          return rec.Id === key || rec.actionId === key;
        });

        if (i >= 0) {
          this.recordsToBeModified[i].Object_RecordType_vod__c =
            val.DeveloperName || null;
          this.recordsToBeModified[i].Object_RecordType_Id_vod__c =
            val.Id || null;
        } else {
          let record = this.dpRecords.find(rec => {
            return rec.Id === key || rec.actionId === key;
          });

          let item = JSON.parse(JSON.stringify(record));

          item.Object_RecordType_vod__c = val.DeveloperName || null;
          item.Object_RecordType_Id_vod__c = val.Id || null;

          this.recordsToBeModified.push(item);
        }
      },
      onVersionChange(val) {
        this.selectedVersion = val;
      },
      newRecord() {
        let record = {
          actionId: uuidv4(),
          selectedAction: ACTION_LABELS.create,
          profile: {
            isEmpty: true
          },
          objRT: {
            isEmpty: true
          },
          Phone_vod__c: false,
          dpPhone: false,
          Studio_Version_vod__c: "",
          isDeploying: false,
          deployStatus: null,
          Name: this.currentApp.name,
          External_Id_vod__c: null,
          Profile_Name_vod__c: null,
          Object_RecordType_vod__c: null,
          type:
            this.types.length > 0
              ? JSON.parse(JSON.stringify(this.types[0]))
              : ""
        };

        if (!this.reportProfileNameFLS.hasEdit) {
          record.profile.isEmpty = false;
        }

        if (!this.reportORTNameFLS.hasEdit) {
          record.objRT.isEmpty = false;
        }

        if (this.platformFLS.hasRead) {
          record.Platform_vod__c = null;
          if (this.platformFLS.hasEdit) {
            record.selectedPlatforms = this.platformValues;
          }
        }

        this.dpRecords.push(record);
        this.recordsToBeModified.push(record);
      },
      doDeploy() {
        this.$bvModal.show("deploy-confirm");
      },
      isRowEditable(action) {
        return (
          action === ACTION_LABELS.update || action === ACTION_LABELS.create
        );
      },
      deleteRow(id, actionId) {
        let component = this;
        this.$bvModal
          .msgBoxConfirm(this.messages.ARE_YOU_SURE.Text_vod__c, {
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.messages.OK.Text_vod__c,
            cancelTitle: this.messages.CANCEL.Text_vod__c,
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          })
          .then(value => {
            if (value) {
              component.dpRecords = component.dpRecords.filter(rec => {
                return id ? rec.Id !== id : rec.actionId !== actionId;
              });

              component.recordsToBeModified = component.recordsToBeModified.filter(
                rec => {
                  return id ? rec.Id !== id : rec.actionId !== actionId;
                }
              );
            }
          })
          .catch(err => {
            console.error(err);
            // An error occurred
          });
      },
      onDeployRecords() {
        this.recordsToBeModified.forEach(rec => {
          rec.isDeploying = true;
        });
      },
      shouldPublishContent(action, hasLightning) {
        return (
          (action === ACTION_LABELS.create ||
            action === ACTION_LABELS.update) &&
          hasLightning
        );
      },
      findActionInList(list, id) {
        return list.find(item => item.actionId === id);
      },
      hasLightning(action) {
        if (action) {
          let field = action.fields.find(
            f => f.htmlReportField === HTML_REPORT_FIELD_NAMES.Platform_vod__c
          );

          if (field) {
            return field.value.includes("Lightning_vod");
          }
        }

        return false;
      },
      setRecordToFailure(list, id) {
        let action = this.findActionInList(list, id);

        if (action) {
          action.isDeploying = false;
          action.deployStatus = "fail";
          action.error = {};
        }
      },
      setRecordToSuccessful(list, serverActionResponse) {
        let action = this.findActionInList(list, serverActionResponse.actionId);

        if (action) {
          action.isDeploying = false;
          action.deployStatus = "success";
          const version = serverActionResponse.fields.find(field => {
            return field.htmlReportField === "Studio_Version_vod__c";
          });
          action.htmlReportId = serverActionResponse.htmlReportId;
          action.Studio_Version_vod__c = version ? version.value : null;
        }
      },
      doneDeployRecords(response, deployList) {
        let hasFailure = false;
        let hasDuplicateExternalId = false;
        let recordsToBeModified = this.recordsToBeModified;
        if (response.data.status === "SUCCESS") {
          response.data.data.forEach(resp => {
            if (resp.status === "SUCCESS" && resp.htmlReportId) {
              this.setRecordToSuccessful(recordsToBeModified, resp);
              let action = this.findActionInList(deployList, resp.actionId);

              if (
                this.shouldPublishContent(
                  resp.action,
                  this.hasLightning(action)
                )
              ) {
                services.publishContent(resp.htmlReportId);
              } else {
                this.setRecordToSuccessful(recordsToBeModified, resp);
              }
            } else {
              if (resp.error) {
                hasDuplicateExternalId =
                  resp.error === "DUPLICATED_EXTERNAL_ID";
              }
              hasFailure = true;
              this.setRecordToFailure(this.recordsToBeModified, resp.actionId);
            }
          });
          if (!hasFailure) {
            this.deployStatusSnapshot = this.recordsToBeModified;
            this.init();
          }
        } else {
          this.recordsToBeModified.forEach(rec => {
            // if overall status is failure, then all failed
            rec.isDeploying = false;
            rec.deployStatus = "fail";
          });
        }

        if (hasDuplicateExternalId) {
          this.$bvToast.toast(
            this.$t("externalIdDuplicated"),
            TOAST_CONFIG.BOTTOM_CENTER_ERROR
          );
        }
      }
    }
  };
</script>

<style scoped>
  .deploy-container {
    padding: 2rem;
  }

  .dv-row {
    display: flex;
    align-items: center;
    padding: 1rem;
  }

  .dv-dd {
    margin-left: 1rem;
  }

  .deployment-section {
    margin-top: 0.1rem;
  }

  .btn-wrapper {
    padding: 0.5rem 1rem;
    display: flex;
  }

  .deploy-btn {
    margin-left: auto;
  }

  .create-tcell {
    display: flex;
  }

  .create-text {
    font-size: 1rem;
    font-weight: 300;
  }

  .delete-wrapper {
    margin-left: auto;
  }

  .delete-row-icon {
    height: 1rem;
    width: 1rem;
    fill: #c23934;
    cursor: pointer;
  }

  .info-icon {
    width: 1rem;
    height: 1rem;
    margin-left: 0.25rem;
    fill: var(--TEXT_GREY_LIGHT);
  }

  .studio-status .loader {
    background-color: transparent;
  }

  .version-match-icon {
    height: 1rem;
    width: 1rem;
    margin-left: 0.5rem;
  }

  .check-icon {
    fill: var(--GREEN);
  }

  .error-icon {
    fill: var(--SF_ERROR_RED);
  }

  .loader-small {
    width: 24px;
    height: 24px;
  }

  .studio-version {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  .record-type-id-col {
    min-width: max-content;
    padding-right: inherit;
  }
</style>
